import type { Home } from '@lmc/ts-common'
import {
  Button,
  Form,
  InputSelect,
  Input as KitInput,
  Select
} from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportsConstants } from 'locale/team'
import type { ReactNode } from 'react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import type { WizardData, WizardScope } from './types'

// Antd Form.Item directly — the ui-kit FormItem hard-wires a 200px left
// label column, which contradicts the wizard's vertical layout in Figma.
const FormItem = AntdForm.Item

const Input = styled(KitInput)`
  &.ant-input,
  & .ant-input {
    border-color: #bfbfbf;
  }
`

export type Step1DetailsProps = {
  value: WizardData
  onChange: (next: WizardData) => void
  onNext: () => void
  onCancel: () => void
  jobTitleSuggestions?: string[]
  scope?: WizardScope
  // Homes for the Site multi-select. The group screen passes its resolved list
  // (redux with a useHomesList fallback); when omitted we read the redux
  // reference list so other callers keep working.
  homes?: Home[]
  testId?: string
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;
`

const LabelText = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RedAsterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
  margin-left: 4px;
`

const EmailLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const Hint = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

const requiredLabel = (text: string): ReactNode => (
  <LabelText>
    {text}
    <RedAsterisk aria-hidden="true">*</RedAsterisk>
  </LabelText>
)

const emailLabel = (text: string, hint: string): ReactNode => (
  <EmailLabelGroup>
    <LabelText>
      {text}
      <RedAsterisk aria-hidden="true">*</RedAsterisk>
    </LabelText>
    <Hint>{hint}</Hint>
  </EmailLabelGroup>
)

export const Step1Details = ({
  value,
  onChange,
  onNext,
  onCancel,
  jobTitleSuggestions,
  scope = 'single-site',
  homes,
  testId
}: Step1DetailsProps) => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.TEAM)

  // The Site multi-select only appears on the group screen's "Add to specific
  // sites" flow; other scopes never resolve a Site field (CM-3388).
  const showSiteField = scope === 'specific-sites'

  // Prefer the homes the caller resolved (group screen: redux with a
  // useHomesList fallback) so the Site options match the rows shown in the
  // list; fall back to the redux reference list for callers that don't pass it.
  const reduxHomes = useSelector(
    (state: { data?: { homes?: Home[] } }) => state.data?.homes
  )
  const resolvedHomes = homes ?? reduxHomes
  const siteOptions = useMemo(
    () =>
      (resolvedHomes ?? [])
        .filter((home) => home?.id)
        .map((home) => ({
          value: home.id as string,
          label: home.name ?? (home.id as string)
        })),
    [resolvedHomes]
  )

  const jobTitleOptions = (jobTitleSuggestions ?? []).map((title) => ({
    label: title,
    value: title
  }))

  // InputSelect runs in tags mode (lets the user type a brand-new title), so
  // onChange yields an array. Job title is a single value: keep only the last
  // entry, cap at 100 chars (the old AutoComplete used maxLength={100}; antd
  // Select tags mode has no maxLength), force the field back to that single
  // tag, and lift it to the parent.
  const handleJobTitleChange = (val: string[] | string) => {
    const arr = Array.isArray(val) ? val : [val]
    const next = (arr[arr.length - 1] ?? '').toString().slice(0, 100)
    form.setFields([{ name: 'jobTitle', value: next ? [next] : [] }])
    set({ jobTitle: next })
  }

  // Keep ant Form state in sync with the controlled value when the parent
  // reopens the wizard (e.g. Back→step1) and replays initial values.
  useEffect(() => {
    form.setFieldsValue({
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      jobTitle: value.jobTitle ? [value.jobTitle] : [],
      homes: value.homes
    })
  }, [
    form,
    value.firstName,
    value.lastName,
    value.email,
    value.jobTitle,
    value.homes
  ])

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        // InputSelect only emits a `required` rule, so re-add the old
        // `whitespace: true` rejection here. Read the committed value from the
        // form (synchronous, authoritative) rather than the `value` prop, which
        // may not have re-rendered yet on a blur-then-click.
        const committed = form.getFieldValue('jobTitle')
        const jobTitle = Array.isArray(committed)
          ? (committed[committed.length - 1] ?? '')
          : (committed ?? '')
        if (!String(jobTitle).trim()) {
          form.setFields([
            {
              name: 'jobTitle',
              errors: [t(reportsConstants.WIZARD_STEP1_JOB_TITLE_ERROR)]
            }
          ])
          return
        }
        set({ jobTitle })
        onNext()
      })
      .catch(() => {
        /* validation errors are rendered by FormItem; do not advance */
      })
  }

  const set = (patch: Partial<WizardData>) => onChange({ ...value, ...patch })

  return (
    <Form
      form={form}
      layout="vertical"
      colon={false}
      requiredMark={false}
      initialValues={{
        firstName: value.firstName,
        lastName: value.lastName,
        email: value.email,
        jobTitle: value.jobTitle ? [value.jobTitle] : [],
        homes: value.homes
      }}
      data-testid={testId ? `${testId}-form` : undefined}
    >
      <Body>
        <FormItem
          name="firstName"
          label={requiredLabel(t(reportsConstants.WIZARD_STEP1_FIRST_NAME))}
          hasFeedback={false}
          rules={[
            {
              required: true,
              message: t(reportsConstants.WIZARD_STEP1_FIRST_NAME_ERROR)
            },
            {
              whitespace: true,
              message: t(reportsConstants.WIZARD_STEP1_FIRST_NAME_ERROR)
            }
          ]}
        >
          <Input
            maxLength={35}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set({ firstName: e.target.value })
            }
          />
        </FormItem>
        <FormItem
          name="lastName"
          label={requiredLabel(t(reportsConstants.WIZARD_STEP1_LAST_NAME))}
          hasFeedback={false}
          rules={[
            {
              required: true,
              message: t(reportsConstants.WIZARD_STEP1_LAST_NAME_ERROR)
            },
            {
              whitespace: true,
              message: t(reportsConstants.WIZARD_STEP1_LAST_NAME_ERROR)
            }
          ]}
        >
          <Input
            maxLength={35}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set({ lastName: e.target.value })
            }
          />
        </FormItem>
        <FormItem
          name="email"
          label={emailLabel(
            t(reportsConstants.WIZARD_STEP1_EMAIL),
            t(reportsConstants.WIZARD_STEP1_EMAIL_HINT)
          )}
          hasFeedback={false}
          rules={[
            {
              required: true,
              message: t(reportsConstants.WIZARD_STEP1_EMAIL_ERROR)
            },
            {
              type: 'email',
              message: t(reportsConstants.WIZARD_STEP1_EMAIL_INVALID_ERROR)
            }
          ]}
        >
          <Input
            type="email"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set({ email: e.target.value })
            }
          />
        </FormItem>
        <InputSelect
          testId="wizard-job-title"
          type="jobTitle"
          required
          layout="column"
          label={requiredLabel(t(reportsConstants.WIZARD_STEP1_JOB_TITLE))}
          errorMessage={t(reportsConstants.WIZARD_STEP1_JOB_TITLE_ERROR)}
          helperText={t(reportsConstants.WIZARD_STEP1_JOB_TITLE_HINT)}
          showSearch
          mode="tags"
          options={jobTitleOptions}
          onChange={handleJobTitleChange}
        />
        {showSiteField && (
          <FormItem
            name="homes"
            label={requiredLabel(t(reportsConstants.WIZARD_STEP1_SITE))}
            hasFeedback={false}
            rules={[
              {
                required: true,
                message: t(reportsConstants.WIZARD_STEP1_SITE_ERROR)
              }
            ]}
          >
            <Select
              mode="multiple"
              placeholder={t(reportsConstants.WIZARD_STEP1_SITE_PLACEHOLDER)}
              options={siteOptions}
              value={value.homes}
              onChange={(next: string[]) => set({ homes: next })}
              style={{ width: '100%' }}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              data-testid={testId ? `${testId}-sites-select` : undefined}
            />
          </FormItem>
        )}
      </Body>
      <Footer>
        <Button onClick={onCancel}>
          {t(generalConstants.CANCEL, { ns: NAMESPACE.GENERAL })}
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {t(reportsConstants.WIZARD_NEXT)}
        </Button>
      </Footer>
    </Form>
  )
}
